// =============================================================================
// Storage Service - HTTP-based file uploads
// =============================================================================
// Simple file upload service that works through backend API
// No direct MinIO client usage in browser

import axios from 'axios';
import api from './api';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Storage Service for file uploads
 * Uses backend API endpoints instead of direct MinIO client
 */
export class StorageService {
  constructor() {
    this.apiClient = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000, // 30 seconds for file uploads
    });
  }

  /**
   * Upload a file through backend API
   */
  async uploadFile(file, options = {}) {
    try {
      const {
        folder = 'uploads',
        isPublic = false,
        onProgress = null
      } = options;

      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', folder);
      formData.append('isPublic', isPublic);

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };

      if (onProgress) {
        config.onUploadProgress = (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted);
        };
      }

      const response = await api.post('/uploads/documents', formData, config);

      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Upload error:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Upload failed'
      };
    }
  }

  /**
   * Get a signed URL for a file
   */
  async getSignedUrl(bucket, path) {
    try {
      const response = await this.apiClient.post('/storage/signed-url', {
        bucket,
        path
      });

      return {
        success: true,
        url: response.data.data.url
      };
    } catch (error) {
      console.error('Error getting signed URL:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  }

  /**
   * List files in a bucket
   */
  async listFiles(bucket, prefix = '') {
    try {
      const response = await this.apiClient.get('/storage/list', {
        params: { bucket, prefix }
      });

      return {
        success: true,
        files: response.data.data.files
      };
    } catch (error) {
      console.error('Error listing files:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  }

  /**
   * Delete a file
   */
  async deleteFile(bucket, path) {
    try {
      const response = await this.apiClient.delete('/storage/file', {
        data: { bucket, path }
      });

      return { success: true };
    } catch (error) {
      console.error('Delete error:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  }

  /**
   * Check if storage is available
   */
  async checkConnection() {
    try {
      const response = await this.apiClient.get('/storage/health');
      return {
        connected: true,
        ...response.data
      };
    } catch (error) {
      return {
        connected: false,
        error: error.message
      };
    }
  }
}

// Export singleton instance
export const storageService = new StorageService();

// For backward compatibility
export default storageService;
